import { auth0 } from "@/services/auth0";

/**
 * IN_APP engagement messages — client side of the engagement orchestrator's /inapp API
 * (client-asks model: on view transitions we ask "anything for me?", render at most one modal,
 * and report back shown / dismissed / converted).
 *
 * DORMANT unless VITE_ENGAGEMENT_API_URL is set: every function is a silent no-op without it.
 * All failures are swallowed (engagement must NEVER break the app) — worst case: no modal.
 *
 * ⚠️ Token acquisition here is deliberately NON-REDIRECTING: it never uses
 * getAccessTokenSilentlyOutsideComponent (whose error handler clears the Auth0 cache and calls
 * loginWithRedirect — an optional engagement lookup must never hijack navigation or force a
 * login). Unauthenticated / failed-token states simply mean "no message".
 */
const ENGAGEMENT_API_URL = import.meta.env.VITE_ENGAGEMENT_API_URL;

export const isInAppMessagingEnabled = () => Boolean(ENGAGEMENT_API_URL);

/** A bearer token or null — never a redirect, never a cache mutation, never a throw. */
const getTokenNonRedirecting = async () => {
  try {
    if (!auth0 || auth0.isAuthenticated?.value !== true) return null;
    return await auth0.getAccessTokenSilently();
  } catch {
    return null;
  }
};

const authorizedFetch = async (path, options = {}) => {
  const token = await getTokenNonRedirecting();
  if (!token) return null;
  return fetch(`${ENGAGEMENT_API_URL}${path}`, {
    ...options,
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers ?? {}),
    },
  });
};

/**
 * Ask the engine for a message for this view.
 * @param {string} viewName vue-router route name (e.g. "home", "conversations")
 * @returns {Promise<null | {deliveryId, messageKey, title, body, ctaLabel, ctaPath, imageUrl, expiresAt}>}
 */
export const askForMessage = async (viewName) => {
  if (!isInAppMessagingEnabled() || !viewName) return null;
  try {
    const res = await authorizedFetch(
      `/inapp/messages?view=${encodeURIComponent(viewName)}`,
    );
    if (!res || !res.ok) return null;
    const body = await res.json();
    return body?.message ?? null;
  } catch {
    return null; // silent by design
  }
};

/**
 * Report what happened with a delivery. Fire-and-forget; idempotent server-side.
 * @param {string} deliveryId
 * @param {"shown"|"dismissed"|"converted"} type
 */
export const reportDeliveryEvent = async (deliveryId, type) => {
  if (!isInAppMessagingEnabled() || !deliveryId) return;
  try {
    await authorizedFetch(
      `/inapp/deliveries/${encodeURIComponent(deliveryId)}/events`,
      {
        method: "POST",
        body: JSON.stringify({ type }),
      },
    );
  } catch {
    // silent by design
  }
};
